import { NotReady, PageHeading, PagePadding, useRequiredParam } from '@life/components'
import { useBook, useReplaceImage } from '@life/frontend-model'
import { Logger } from '@life/model'
import { lazy, Suspense } from 'react'
import { useNavigate } from 'react-router-dom'

const logger = new Logger('pintura')

// react-filerobot-image-editor pulls in react-konva and styled-components, which aren't
// used anywhere else in this app. Loading it via a dynamic import lets Parcel split it into
// its own chunk, so that extra JS only ships to users who actually open the photo editor.
const FilerobotImageEditor = lazy(() => import('react-filerobot-image-editor'))

type SavedImageData = {
  fullName?: string
  mimeType: string
  imageBase64?: string
}

export function Pintura(): JSX.Element {
  const [bookSlug, imageId] = useRequiredParam(['bookSlug', 'imageId'])
  const { book, isLoading, error } = useBook(bookSlug)
  const image = book?.findImage(imageId)
  const { upload } = useReplaceImage(image)
  const navigate = useNavigate()
  // End of Hooks

  if (isLoading || error || !book) return <NotReady type="Book" id={bookSlug} isLoading={isLoading} error={error} />
  if (!image) return <NotReady type="Image" id={imageId} notFound />

  async function handleSave(savedImageData: SavedImageData): Promise<void> {
    if (!savedImageData.imageBase64) {
      logger.error('save', 'no imageBase64 returned from editor', savedImageData)
      return
    }

    const response = await fetch(savedImageData.imageBase64)
    const blob = await response.blob()
    const fileName = savedImageData.fullName ?? image?.name ?? 'photo'
    const file = new File([blob], fileName, { type: savedImageData.mimeType })

    upload(file)
    navigate('..')
  }

  return (
    <PagePadding className="flex flex-col" wide>
      <PageHeading title="Edit Photo">Edit Photo</PageHeading>
      <div className="w-full flex-1" style={{ minHeight: 600 }}>
        <Suspense fallback={<NotReady type="Image" id={imageId} isLoading />}>
          <FilerobotImageEditor
            source={image.originalUrl}
            onSave={handleSave}
            onClose={() => navigate('..')}
            defaultSavedImageName={image.name}
            savingPixelRatio={1}
            previewPixelRatio={1}
          />
        </Suspense>
      </div>
    </PagePadding>
  )
}
